// ============================================================
//  BEGIN CUSTOM EDIT: Bundle Add-to-Cart | Date: 2026-03-09
//  What: Custom element to bundle complementary products with main ATC
// ============================================================

if (!window.customElements.get('bundle-add-to-cart')) {
  window.customElements.define('bundle-add-to-cart', class extends HTMLElement {
    #formEl = null;
    #boundSubmitHandler = null;
    #observer = null;

    connectedCallback() {
      this.#findForm();
      this.#attachCheckboxListeners();
      this.#attachCardClickListeners();
      this.#observeAsyncLoad();
    }

    disconnectedCallback() {
      if (this.#formEl && this.#boundSubmitHandler) {
        this.#formEl.removeEventListener('submit', this.#boundSubmitHandler, true);
      }
      if (this.#observer) {
        this.#observer.disconnect();
        this.#observer = null;
      }
    }

    // --- Form Discovery ---

    #findForm() {
      const formId = this.dataset.formId;
      if (!formId) return;

      this.#formEl = document.getElementById(formId);
      if (!this.#formEl) return;

      this.#boundSubmitHandler = this.#onFormSubmit.bind(this);
      this.#formEl.addEventListener('submit', this.#boundSubmitHandler, true);
    }

    // --- Async Load Observer ---
    // Complementary products load via section rendering API.
    // When they arrive, ProductRecommendations replaces its children.
    // We observe for new checkboxes and re-attach listeners.

    #observeAsyncLoad() {
      this.#observer = new MutationObserver(() => {
        this.#attachCheckboxListeners();
        this.#attachCardClickListeners();
      });
      this.#observer.observe(this, { childList: true, subtree: true });
    }

    // --- Checkbox Handling ---

    #attachCheckboxListeners() {
      this.querySelectorAll('.bundle-checkbox__input').forEach(cb => {
        if (cb._bundleListenerAttached) return;
        cb._bundleListenerAttached = true;
        cb.addEventListener('change', () => this.#updateCounter());
      });
    }

    // --- Card Click → Toggle Checkbox ---
    // Clicking the card toggles the checkbox, but clicking a link navigates normally.

    #attachCardClickListeners() {
      this.querySelectorAll('.horizontal-product').forEach(card => {
        if (card._bundleClickAttached) return;
        card._bundleClickAttached = true;
        card.addEventListener('click', (e) => {
          const cb = card.querySelector('.bundle-checkbox__input');
          if (!cb) return;
          if (e.target.closest('a') || e.target.closest('.bundle-checkbox')) return;
          cb.checked = !cb.checked;
          cb.dispatchEvent(new Event('change'));
        });
      });
    }

    #getCheckedItems() {
      return Array.from(this.querySelectorAll('.bundle-checkbox__input:checked'));
    }

    #updateCounter() {
      const checkedItems = this.#getCheckedItems();
      const count = checkedItems.length;
      const countEl = this.querySelector('[data-bundle-count]');
      if (!countEl) return;

      const totalCheckboxes = this.querySelectorAll('.bundle-checkbox__input').length;
      if (count > 0 && totalCheckboxes > 1) {
        const totalCents = checkedItems.reduce((sum, cb) => sum + parseInt(cb.dataset.bundlePrice || '0', 10), 0);
        const selectedText = (this.dataset.selectedText || '__COUNT__ selected').replace('__COUNT__', count);
        countEl.textContent = '';
        countEl.append(`${selectedText} `);
        const priceSpan = document.createElement('span');
        priceSpan.className = 'bundle-title__price';
        priceSpan.textContent = `(${this.#formatMoney(totalCents)})`;
        countEl.append(priceSpan);
        countEl.hidden = false;
      } else {
        countEl.textContent = '';
        countEl.hidden = true;
      }
    }

    #formatMoney(cents) {
      const settings = window.themeVariables?.settings || {};
      const currencyCodeEnabled = settings.currencyCodeEnabled || false;
      const template = currencyCodeEnabled
        ? (settings.moneyWithCurrencyFormat || '{{amount}}')
        : (settings.moneyFormat || '{{amount}}');
      const amount = (cents / 100).toFixed(2);
      return template
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toString())
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    // --- Form Submit Intercept ---

    async #onFormSubmit(event) {
      const checkedItems = this.#getCheckedItems();
      if (checkedItems.length === 0) return; // let default ProductForm handler run

      // Stop the original ProductForm handler
      event.preventDefault();
      event.stopImmediatePropagation();

      const form = this.#formEl;
      const submitButtons = Array.from(form.elements).filter(el => el.type === 'submit');
      submitButtons.forEach(btn => btn.setAttribute('aria-busy', 'true'));

      // Build items array: main product + checked complementary products
      const mainVariantId = form.querySelector('[name="id"]').value;
      const mainQuantity = form.querySelector('[name="quantity"]')?.value || '1';

      const items = [
        { id: parseInt(mainVariantId), quantity: parseInt(mainQuantity) }
      ];

      // Add line item properties if present (e.g. custom lace length)
      const mainProperties = {};
      form.querySelectorAll('[name^="properties["]').forEach(input => {
        const key = input.name.match(/properties\[(.+)\]/)?.[1];
        if (key && input.value) mainProperties[key] = input.value;
      });
      if (Object.keys(mainProperties).length > 0) {
        items[0].properties = mainProperties;
      }

      checkedItems.forEach(cb => {
        items.push({
          id: parseInt(cb.dataset.bundleVariantId),
          quantity: 1
        });
      });

      // Prepare bundled sections (matches theme behavior)
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: `${Shopify.routes.root}variants/${mainVariantId}`
          })
        });

        submitButtons.forEach(btn => btn.removeAttribute('aria-busy'));
        const responseJson = await response.json();

        if (response.ok) {
          // Cart type page: redirect
          if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
            window.location.href = `${Shopify.routes.root}cart`;
            return;
          }

          const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          cartContent['sections'] = responseJson['sections'];

          // Dispatch same events as ProductForm
          form.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
              cart: cartContent
            }
          }));

          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent
            }
          }));

          // Uncheck all after successful add
          checkedItems.forEach(cb => { cb.checked = false; });
          this.#updateCounter();
        } else {
          form.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: responseJson['description'] }
          }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } catch (err) {
        submitButtons.forEach(btn => btn.removeAttribute('aria-busy'));
        console.error('Bundle ATC error:', err);
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: 'An error occurred. Please try again.' }
        }));
      }
    }
  });
}

// ============================================================
//  END CUSTOM EDIT
// ============================================================
